import config from "../Configurations";
import { getWithExpiry } from "./common";
import { collectClientFingerprint } from "./clientFingerprint";

const SESSION_STORAGE_KEY = "pivony_product_session_id";
const SESSION_STARTED_KEY = "pivony_product_session_started";
const FLUSH_INTERVAL_MS = 5000;
const MAX_QUEUE_SIZE = 40;
const MIN_PAGE_DWELL_MS = 1000;
const PAGE_ENGAGEMENT_SCROLL_THRESHOLD_PX = 80;
const PAGE_DWELL_META_EVENTS = new Set([
  "page_view",
  "page_dwell",
  "session_start",
  "advisor_message_sent",
  "page_interaction",
]);
const ADVISOR_PAGE_ENGAGEMENT_ACTIVE_COUNT = 3;

/** Standalone events that count as page engagement (shown in timeline + dwell stats). */
export const isGranularEngagementEvent = (eventName) => {
  const name = String(eventName ?? "").toLowerCase();
  if (!name) return false;
  return !PAGE_DWELL_META_EVENTS.has(name);
};

let queue = [];
let flushTimer = null;
let userId = null;
let enabled = true;
let currentPagePath = null;
let currentPageEnteredAt = null;
let currentPageActionCount = 0;
let accumulatedDwellMs = 0;
let pageEngagementSignals = new Set();
let engagementListenersBound = false;

const getJwtToken = () => {
  const tokenStr = window.localStorage.getItem("jwt_token");
  if (!tokenStr) return null;
  try {
    const item = JSON.parse(tokenStr);
    const now = new Date();
    if (now.getTime() > item.expiry) return null;
    return item.value;
  } catch {
    return null;
  }
};

export const getProductSessionId = () => {
  try {
    let sid = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!sid) {
      sid = `ps_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_STORAGE_KEY, sid);
      sessionStorage.removeItem(SESSION_STARTED_KEY);
    }
    return sid;
  } catch {
    return `ps_${Date.now()}`;
  }
};

const buildPayload = (events) => ({
  session_id: getProductSessionId(),
  events: events.map((ev) => ({
    ...ev,
    session_id: ev.session_id || getProductSessionId(),
    client_ts: ev.client_ts || new Date().toISOString(),
  })),
});

const sendEvents = async (events, { keepalive = false } = {}) => {
  if (!events.length || !enabled) return;
  const token = getJwtToken();
  if (!token) return;

  const body = JSON.stringify(buildPayload(events));
  const url = `${config.BASE_URL}organization_engagement/events`;

  try {
    if (keepalive && typeof fetch !== "undefined") {
      await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body,
        keepalive: true,
      });
      return;
    }
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body,
    });
    if (response.status === 503) {
      enabled = false;
    }
  } catch {
    // Best-effort analytics; never block UX.
  }
};

export const flushProductAnalytics = async ({ keepalive = false } = {}) => {
  if (!queue.length) return;
  const batch = queue.splice(0, MAX_QUEUE_SIZE);
  await sendEvents(batch, { keepalive });
};

const scheduleFlush = () => {
  if (flushTimer) return;
  flushTimer = window.setTimeout(async () => {
    flushTimer = null;
    await flushProductAnalytics();
    if (queue.length) scheduleFlush();
  }, FLUSH_INTERVAL_MS);
};

export const trackProductEvent = (
  eventName,
  {
    event_category: eventCategory = "other",
    page_path: pagePath,
    resource_type: resourceType,
    resource_id: resourceId,
    properties,
  } = {},
) => {
  if (!enabled || !eventName) return;
  const normalizedName = String(eventName).toLowerCase();
  if (
    currentPagePath &&
    !PAGE_DWELL_META_EVENTS.has(normalizedName)
  ) {
    currentPageActionCount += 1;
  }
  queue.push({
    event_name: normalizedName,
    event_category: eventCategory,
    page_path: pagePath || window.location.pathname,
    resource_type: resourceType,
    resource_id: resourceId != null ? String(resourceId) : undefined,
    properties: properties || {},
  });
  if (queue.length >= MAX_QUEUE_SIZE) {
    void flushProductAnalytics();
  } else {
    scheduleFlush();
  }
};

let lastTrackedPagePath = null;

const pausePageDwellSegment = () => {
  if (currentPageEnteredAt) {
    accumulatedDwellMs += Date.now() - currentPageEnteredAt;
    currentPageEnteredAt = null;
  }
};

const resetPageEngagementSignals = () => {
  pageEngagementSignals = new Set();
};

const recordPageEngagementAction = (signalKey, weight = 1) => {
  if (!currentPagePath || !signalKey || !enabled) return false;
  const key = `${currentPagePath}::${signalKey}`;
  if (pageEngagementSignals.has(key)) return false;
  pageEngagementSignals.add(key);
  currentPageActionCount += Math.max(1, Number(weight) || 1);
  trackProductEvent("page_interaction", {
    event_category: "navigation",
    page_path: currentPagePath,
    properties: { signal: signalKey },
  });
  return true;
};

export const trackPageEngagementSignal = (signalKey) => {
  recordPageEngagementAction(signalKey);
};

const handleEngagementScroll = (event) => {
  if (!currentPagePath) return;
  const target = event.target;
  let scrollTop = 0;
  let targetKey = "window";

  if (target === document || target === document.documentElement) {
    scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
  } else if (target instanceof Element) {
    scrollTop = target.scrollTop || 0;
    targetKey =
      target.getAttribute("data-engagement-scroll-root") ||
      target.id ||
      target.tagName.toLowerCase();
  }

  if (scrollTop >= PAGE_ENGAGEMENT_SCROLL_THRESHOLD_PX) {
    recordPageEngagementAction(`scroll:${targetKey}`);
  }
};

const handleEngagementHover = (event) => {
  if (!currentPagePath) return;
  const target = event.target;
  if (!(target instanceof Element)) return;
  const widget = target.closest("[data-widget-id], [data-engagement-widget]");
  if (!widget) return;
  const widgetId =
    widget.getAttribute("data-widget-id") ||
    widget.getAttribute("data-engagement-widget");
  if (!widgetId) return;
  recordPageEngagementAction(`hover:widget:${widgetId}`);
};

const setupPageEngagementSignalListeners = () => {
  if (engagementListenersBound || typeof document === "undefined") return;
  engagementListenersBound = true;
  document.addEventListener("scroll", handleEngagementScroll, {
    capture: true,
    passive: true,
  });
  document.addEventListener("mouseover", handleEngagementHover, {
    capture: true,
    passive: true,
  });
};

const resetPageDwellState = () => {
  currentPagePath = null;
  currentPageEnteredAt = null;
  currentPageActionCount = 0;
  accumulatedDwellMs = 0;
  resetPageEngagementSignals();
};

export const flushPageDwell = (exitReason = "navigation") => {
  if (!currentPagePath) return;
  pausePageDwellSegment();
  const durationMs = accumulatedDwellMs;
  const actionCount = currentPageActionCount;
  const pagePath = currentPagePath;
  resetPageDwellState();

  if (durationMs < MIN_PAGE_DWELL_MS) return;

  trackProductEvent("page_dwell", {
    event_category: "navigation",
    page_path: pagePath,
    properties: {
      duration_ms: durationMs,
      action_count: actionCount,
      exit_reason: exitReason,
    },
  });
};

export const trackPageView = (path) => {
  const pagePath =
    path ||
    `${window.location.pathname || ""}${window.location.search || ""}`;
  if (!pagePath || pagePath === lastTrackedPagePath) return;

  flushPageDwell("navigation");

  const previousPage = lastTrackedPagePath;
  lastTrackedPagePath = pagePath;
  currentPagePath = pagePath;
  currentPageEnteredAt = Date.now();
  currentPageActionCount = 0;
  accumulatedDwellMs = 0;
  resetPageEngagementSignals();

  trackProductEvent("page_view", {
    event_category: "navigation",
    page_path: pagePath,
    properties: previousPage ? { previous_page: previousPage } : {},
  });
};

export const trackLogin = () => {
  trackProductEvent("login", { event_category: "auth" });
};

let routingUnlisten = null;

export const setupProductAnalyticsRouting = (historyRef) => {
  if (!historyRef?.listen) return () => {};
  if (routingUnlisten) routingUnlisten();

  const emit = (location) => {
    const path = `${location.pathname || ""}${location.search || ""}`;
    trackPageView(path);
  };

  emit(historyRef.location || { pathname: window.location.pathname });
  routingUnlisten = historyRef.listen((location) => emit(location));

  return () => {
    if (routingUnlisten) {
      routingUnlisten();
      routingUnlisten = null;
    }
  };
};

export const initProductAnalytics = (uid, historyRef) => {
  userId = uid || userId;
  if (!userId || !getJwtToken()) return;

  enabled = true;
  if (historyRef) {
    setupProductAnalyticsRouting(historyRef);
  }

  try {
    const sid = getProductSessionId();
    const startedKey = `${SESSION_STARTED_KEY}_${sid}`;
    if (!sessionStorage.getItem(startedKey)) {
      sessionStorage.setItem(startedKey, "1");
      trackProductEvent("session_start", {
        event_category: "session",
        properties: {
          referrer: document.referrer || null,
          viewport_w: window.innerWidth,
          viewport_h: window.innerHeight,
          client_fingerprint: collectClientFingerprint(),
        },
      });
    }
  } catch {
    trackProductEvent("session_start", {
      event_category: "session",
      properties: {
        client_fingerprint: collectClientFingerprint(),
      },
    });
  }

  if (!window.__pivonyAnalyticsUnloadBound) {
    window.__pivonyAnalyticsUnloadBound = true;
    const flushOnLeave = () => {
      flushPageDwell("pagehide");
      void flushProductAnalytics({ keepalive: true });
    };
    window.addEventListener("pagehide", flushOnLeave);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        flushPageDwell("hidden");
        void flushProductAnalytics({ keepalive: true });
      } else if (document.visibilityState === "visible" && lastTrackedPagePath) {
        currentPagePath = lastTrackedPagePath;
        currentPageEnteredAt = Date.now();
        currentPageActionCount = 0;
        accumulatedDwellMs = 0;
        resetPageEngagementSignals();
      }
    });
  }

  setupPageEngagementSignalListeners();
  scheduleFlush();
};

export const resetProductAnalytics = () => {
  queue = [];
  userId = null;
  enabled = true;
  lastTrackedPagePath = null;
  resetPageDwellState();
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
};

const openedDashboardsThisSession = new Set();
const openedAiReportsThisSession = new Set();

export const trackDashboardOpened = (dashboardId, { name, view } = {}) => {
  if (dashboardId == null || dashboardId === "") return;
  trackProductEvent("dashboard_opened", {
    event_category: "dashboard",
    resource_type: "dashboard",
    resource_id: String(dashboardId),
    properties: {
      ...(name ? { dashboard_name: name } : {}),
      ...(view ? { view } : {}),
    },
  });
};

export const trackDashboardOpenedOnce = (dashboardId, meta = {}) => {
  const id = String(dashboardId);
  if (!id || openedDashboardsThisSession.has(id)) return;
  openedDashboardsThisSession.add(id);
  trackDashboardOpened(dashboardId, meta);
};

export const trackDashboardFilterChanged = (
  dashboardId,
  filters,
  { scope = "dashboard" } = {},
) => {
  trackProductEvent("dashboard_filter_changed", {
    event_category: scope === "welcome" ? "welcome" : "dashboard",
    resource_type: dashboardId != null ? "dashboard" : undefined,
    resource_id: dashboardId != null ? String(dashboardId) : undefined,
    properties: {
      scope,
      filter_keys: Object.keys(filters || {}),
    },
  });
};

export const trackReviewExported = (dashboardId, fileType, { onlySequences } = {}) => {
  trackProductEvent("review_exported", {
    event_category: "export",
    resource_type: "dashboard",
    resource_id: String(dashboardId),
    properties: {
      file_type: fileType,
      ...(onlySequences ? { only_sequences: true } : {}),
    },
  });
};

export const trackWelcomePageViewed = () => {
  trackProductEvent("welcome_page_viewed", {
    event_category: "welcome",
    page_path: window.location.pathname,
  });
};

export const trackWelcomeWidgetRegenerated = (
  widgetId,
  { metricId, groupId, kpiOnly } = {},
) => {
  trackProductEvent("welcome_widget_regenerated", {
    event_category: "welcome",
    resource_type: "welcome_widget",
    resource_id: String(widgetId),
    properties: {
      ...(metricId != null ? { metric_id: metricId } : {}),
      ...(groupId != null ? { group_id: groupId } : {}),
      ...(kpiOnly ? { kpi_only: true } : {}),
    },
  });
};

export const trackAiReportOpened = (reportId, { brand, status } = {}) => {
  trackProductEvent("ai_report_opened", {
    event_category: "ai_services",
    resource_type: "ai_report",
    resource_id: String(reportId),
    properties: {
      ...(brand ? { brand } : {}),
      ...(status ? { status } : {}),
    },
  });
};

export const trackAiReportOpenedOnce = (reportId, meta = {}) => {
  const id = String(reportId);
  if (!id || openedAiReportsThisSession.has(id)) return;
  openedAiReportsThisSession.add(id);
  trackAiReportOpened(reportId, meta);
};

export const trackAdvisorSessionStarted = (sessionId, { source = "new_chat" } = {}) => {
  if (!sessionId) return;
  trackProductEvent("advisor_session_started", {
    event_category: "advisor",
    resource_type: "advisor_session",
    resource_id: String(sessionId),
    properties: { source },
  });
};

export const trackAdvisorMessageSent = (
  sessionId,
  { onAdvisorPage = false, messageLength = 0 } = {},
) => {
  if (!sessionId) return;
  trackProductEvent("advisor_message_sent", {
    event_category: "advisor",
    resource_type: "advisor_session",
    resource_id: String(sessionId),
    page_path: window.location.pathname,
    properties: {
      on_advisor_page: Boolean(onAdvisorPage),
      ...(messageLength > 0 ? { message_length: messageLength } : {}),
    },
  });
  if (onAdvisorPage) {
    recordPageEngagementAction(
      "advisor:message",
      ADVISOR_PAGE_ENGAGEMENT_ACTIVE_COUNT,
    );
  }
};

export const trackKpiViewAction = (
  action,
  {
    kpiId,
    kpiTitle,
    metricKey,
    teamId,
    groupPage,
    groupName,
    date,
    alertMode,
    ...rest
  } = {},
) => {
  if (!action) return;
  trackProductEvent("kpi_view_action", {
    event_category: "kpi_view",
    resource_type: kpiId != null ? "kpi" : undefined,
    resource_id: kpiId != null ? String(kpiId) : undefined,
    properties: {
      action: String(action),
      ...(kpiTitle ? { kpi_title: String(kpiTitle).slice(0, 200) } : {}),
      ...(metricKey ? { metric_key: String(metricKey) } : {}),
      ...(teamId != null ? { team_id: String(teamId) } : {}),
      ...(groupPage != null ? { group_page: Number(groupPage) } : {}),
      ...(groupName ? { group_name: String(groupName).slice(0, 120) } : {}),
      ...(date ? { filter_date: String(date).slice(0, 10) } : {}),
      ...(alertMode ? { alert_mode: String(alertMode) } : {}),
      ...rest,
    },
  });
};

export const trackKpiCreated = ({
  topicCount = 0,
  dashboardCount = 0,
  pivotCount = 0,
  mode = "combined",
  teamId,
} = {}) => {
  trackProductEvent("kpi_created", {
    event_category: "kpi_view",
    properties: {
      topic_count: Number(topicCount) || 0,
      dashboard_count: Number(dashboardCount) || 0,
      pivot_count: Number(pivotCount) || 0,
      mode: String(mode),
      ...(teamId != null ? { team_id: String(teamId) } : {}),
    },
  });
};

export const trackKpiGroupAction = (
  action,
  { groupPage, groupName, teamId } = {},
) => {
  if (!action) return;
  trackProductEvent("kpi_group_action", {
    event_category: "kpi_view",
    properties: {
      action: String(action),
      ...(groupPage != null ? { group_page: Number(groupPage) } : {}),
      ...(groupName ? { group_name: String(groupName).slice(0, 120) } : {}),
      ...(teamId != null ? { team_id: String(teamId) } : {}),
    },
  });
};

export const trackWelcomeWidgetAction = (
  action,
  {
    widgetId,
    widgetName,
    metricId,
    groupId,
    source,
    ...rest
  } = {},
) => {
  if (!action) return;
  trackProductEvent("welcome_widget_action", {
    event_category: "welcome",
    resource_type: widgetId != null ? "welcome_widget" : undefined,
    resource_id: widgetId != null ? String(widgetId) : undefined,
    properties: {
      action: String(action),
      ...(widgetName ? { widget_name: String(widgetName).slice(0, 200) } : {}),
      ...(metricId != null ? { metric_id: Number(metricId) } : {}),
      ...(groupId != null ? { group_id: Number(groupId) } : {}),
      ...(source ? { source: String(source) } : {}),
      ...rest,
    },
  });
};

export const trackWelcomeReviewsOpened = ({
  widgetId,
  widgetName,
  metricId,
  source,
  label,
  topicLabel,
} = {}) => {
  trackProductEvent("welcome_reviews_opened", {
    event_category: "welcome",
    resource_type: widgetId != null ? "welcome_widget" : undefined,
    resource_id: widgetId != null ? String(widgetId) : undefined,
    properties: {
      ...(widgetName ? { widget_name: String(widgetName).slice(0, 200) } : {}),
      ...(metricId != null ? { metric_id: Number(metricId) } : {}),
      ...(source ? { source: String(source) } : {}),
      ...(label ? { label: String(label).slice(0, 200) } : {}),
      ...(topicLabel ? { topic_label: String(topicLabel).slice(0, 200) } : {}),
    },
  });
};

export const trackOrgAddonAction = (
  action,
  {
    source,
    step,
    mode,
    error,
    returnPath,
    ...rest
  } = {},
) => {
  if (!action) return;
  trackProductEvent("org_addon_action", {
    event_category: "settings",
    page_path: window.location.pathname,
    properties: {
      action: String(action),
      ...(source ? { source: String(source) } : {}),
      ...(step ? { step: String(step) } : {}),
      ...(mode ? { mode: String(mode) } : {}),
      ...(error ? { error: String(error).slice(0, 500) } : {}),
      ...(returnPath ? { return_path: String(returnPath) } : {}),
      ...rest,
    },
  });
};

const viewedAddonLocksThisSession = new Set();

export const trackAddonLockViewed = (addon, { pagePath } = {}) => {
  const key = String(addon || "").trim().toLowerCase();
  if (!key || viewedAddonLocksThisSession.has(key)) return;
  viewedAddonLocksThisSession.add(key);
  trackProductEvent("addon_lock_viewed", {
    event_category: "upsell",
    page_path: pagePath || window.location.pathname,
    properties: { addon: key },
  });
};

export const trackAddonLockCtaClicked = (addon, cta, { pagePath, ...rest } = {}) => {
  const addonKey = String(addon || "").trim().toLowerCase();
  const ctaKey = String(cta || "").trim().toLowerCase();
  if (!addonKey || !ctaKey) return;
  trackProductEvent("addon_lock_cta_clicked", {
    event_category: "upsell",
    page_path: pagePath || window.location.pathname,
    properties: {
      addon: addonKey,
      cta: ctaKey,
      ...rest,
    },
  });
};

export const trackProspectTrialEvent = (eventName, properties = {}) => {
  const name = String(eventName || "").trim();
  if (!name) return;
  trackProductEvent(name, {
    event_category: "sonic_prospect_trial",
    page_path: window.location.pathname,
    properties,
  });
};
